/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: DQE.RULES
 *
 * Setup interface for the generic Data Quality engine. Maintains
 * dqe_rule (required / format / cross-field rules per entity).
 * Enforcement is automatic: csm_common_routine_with_context evaluates every
 * active rule for the target entity before each mutation commits.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import type { AuthSession } from '../../types/auth';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { useFormatDateTime } from '../../lib/format_date';
import { loadBreachSnapshots, runBreachScan, summariseBreaches, type BreachSnapshot } from '../../lib/dqe_breach_scan';
import { DqeBreachReviewModal } from './DqeBreachReviewModal';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DQE.RULES';

registerUdcHost('DQE.RULES', 'dqe_rule', 'Data Quality Rules');

type RuleType = 'REQUIRED' | 'FORMAT' | 'CROSS_FIELD';
type Severity = 'ERROR' | 'WARNING';
type AppliesOn = 'ADD' | 'CHANGE' | 'BOTH';

interface RuleRow {
  data_quality_rule_id: number;
  internal_code: string;
  entity_name: string;
  rule_type: RuleType;
  severity: Severity;
  applies_on: AppliesOn;
  rule_def: unknown;
  error_message: string | null;
  is_active: 'Y' | 'N';
  description: string | null;
}

const DEFAULT_DEF: Record<RuleType, unknown> = {
  REQUIRED: { column: 'email' },
  FORMAT: { column: 'email', format: 'email' },
  CROSS_FIELD: { predicate: { type: 'compare', left_field: 'end_date', op: '>=', right_field: 'start_date', as: 'date' } },
};

const SEVERITY_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  ERROR:   { labelKey: 'dqe.severity_badge.error',   label: 'Error',   variant: 'danger' },
  WARNING: { labelKey: 'dqe.severity_badge.warning', label: 'Warning', variant: 'warning' },
};

const RULE_FIELD_DEFS: FieldDef[] = [
  { key: 'code', type: 'text', label: 'Internal Code', required: true, col_span: 1 },
  { key: 'entity', type: 'text', label: 'Entity (table)', required: true, col_span: 1 },
  { key: 'ruleType', type: 'select', label: 'Rule Type', col_span: 1 },
  { key: 'severity', type: 'select', label: 'Severity', col_span: 1 },
  { key: 'appliesOn', type: 'select', label: 'Applies On', col_span: 1 },
];
const RULE_CORE_LAYOUT = toUldCoreLayout(RULE_FIELD_DEFS);

const RULE_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Code',     defaultWidth: 200 },
  { key: 'entity_name',   header: 'Entity',   defaultWidth: 200 },
  { key: 'rule_type',     header: 'Type',     defaultWidth: 130 },
  { key: 'severity',      header: 'Severity', defaultWidth: 110 },
  { key: 'applies_on',    header: 'On',       defaultWidth: 90  },
  { key: 'breach_count',    header: 'Breaches',     defaultWidth: 100 },
  { key: 'last_scanned_at', header: 'Last scanned', defaultWidth: 170 },
  { key: 'is_active',     header: 'Active',   defaultWidth: 80  },
];

export interface DataQualityRulesPageProps { session: AuthSession; }

export function DataQualityRulesPage({ session }: DataQualityRulesPageProps) {
  return (
    <PersonalizationProvider pageCode="DQE.RULES" session={session}>
      <DataQualityRulesPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DataQualityRulesPageInner({ session }: DataQualityRulesPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const fmtDateTime = useFormatDateTime();
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RuleRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('');
  // Breach reporting: per-rule persisted snapshots (count + last scan), the scan
  // action, and the drill-through target. Snapshots join to rows by rule id.
  const [snapshots, setSnapshots] = useState<Map<number, BreachSnapshot>>(new Map());
  const [scanning, setScanning] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<RuleRow | null>(null);
  // §17.B13: related-children expand/contract. A DQ rule targets an entity by
  // name and loads no child row collections, so the disclosure renders inert.
  const detail = useExpandedRows<number>();
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [
    // TODO: wire relations
  ];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ entity: string[]; type: string[]; severity: string[]; appliesOn: string[]; active: string[] }>(
    { entity: [], type: [], severity: [], appliesOn: [], active: ['Y'] },
  );

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('dqe_rule')
        .select('data_quality_rule_id, internal_code, entity_name, rule_type, severity, applies_on, rule_def, error_message, is_active, description')
        .order('entity_name').order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as RuleRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const reloadSnapshots = async () => {
    const { byRule, error: e } = await loadBreachSnapshots();
    if (!e) setSnapshots(byRule);
  };
  useEffect(() => { void reload(); void reloadSnapshots(); }, []);

  // Recompute breach counts for every active rule, persist them through the
  // Common Routine, and refresh the snapshots (errors-only messaging: success is
  // reflected silently in the banner/columns, §17.B7).
  const runScan = async () => {
    setScanning(true); setError(null);
    const { byRule, error: e } = await runBreachScan(session);
    setScanning(false);
    if (e) { setError(e.message); return; }
    setSnapshots(byRule);
  };

  const summary = useMemo(() => summariseBreaches(snapshots.values()), [snapshots]);

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('dqe_rule', 'DQE.RULES', { pkColumn: 'data_quality_rule_id', rows: rows as unknown as Record<string, unknown>[] });

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.filter(Boolean))].sort();
    return {
      entity:    uniq(rows.map(r => r.entity_name)).map(v => ({ value: v, label: v })),
      type:      uniq(rows.map(r => r.rule_type)).map(v => ({ value: v, label: v })),
      severity:  uniq(rows.map(r => r.severity)).map(v => ({ value: v, label: v })),
      appliesOn: uniq(rows.map(r => r.applies_on)).map(v => ({ value: v, label: v })),
      active:    [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }],
    };
  }, [rows, t]);

  const facetsActive = facets.entity.length + facets.type.length + facets.severity.length + facets.appliesOn.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => {
    setFilter('');
    setFacets({ entity: [], type: [], severity: [], appliesOn: [], active: [] });
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matchesUdc = (r: RuleRow) => udcGrid.matchRow(r as unknown as Record<string, unknown>);
    // OR within a facet, AND across facets.
    const matchesFacets = (r: RuleRow) =>
      (facets.entity.length    === 0 || facets.entity.includes(r.entity_name)) &&
      (facets.type.length      === 0 || facets.type.includes(r.rule_type)) &&
      (facets.severity.length  === 0 || facets.severity.includes(r.severity)) &&
      (facets.appliesOn.length === 0 || facets.appliesOn.includes(r.applies_on)) &&
      (facets.active.length    === 0 || facets.active.includes(r.is_active));
    const base = (!q ? rows : rows.filter(r =>
      r.internal_code.toLowerCase().includes(q) ||
      r.entity_name.toLowerCase().includes(q) ||
      r.rule_type.toLowerCase().includes(q)));
    return base.filter(matchesUdc).filter(matchesFacets);
  }, [rows, filter, facets, udcGrid]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('dqe.title', 'Data Quality Rules')}
          description={t('dqe.list.description', 'Rules the Common Routine enforces on every write, with on-demand breach scanning.')}
          actions={
            <>
              <button type="button" onClick={() => void runScan()} disabled={scanning} className="btn-secondary flex items-center gap-1.5 disabled:opacity-60">
                {scanning ? t('dqe.scanning', 'Scanning…') : t('dqe.scan_now', 'Scan breaches')}
              </button>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('dqe.refresh', 'Refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('dqe.new_rule', 'New Rule')}
              </button>)}
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('dqe.stat.total', 'Total rules') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('dqe.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('dqe.load_failed', "Couldn't load data quality rules.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        {summary.lastScannedAt && (
          <div className={
            'mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-sm border px-3 py-2 text-csm-sm ' +
            (summary.totalBreaches > 0
              ? 'border-csm-danger-border bg-csm-danger-soft text-csm-danger'
              : 'border-csm-border bg-csm-surface-alt text-csm-ink-soft')
          }>
            <span className="font-medium">
              {t('dqe.summary.headline', '{{rules}} rules breaching, {{total}} total breaches', { rules: summary.rulesBreaching, total: summary.totalBreaches })}
            </span>
            <span className="text-csm-xs opacity-80">
              {t('dqe.summary.as_of', 'as of {{when}}', { when: fmtDateTime(summary.lastScannedAt) })}
            </span>
          </div>
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('dqe.filter_placeholder', 'Filter rules…')}
                className="h-8 w-60 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('dqe.col.entity', 'Entity')}     options={facetOptions.entity}    selected={facets.entity}    onChange={v => setFacets(f => ({ ...f, entity: v }))} />
            <FacetFilter label={t('dqe.col.type', 'Type')}         options={facetOptions.type}      selected={facets.type}      onChange={v => setFacets(f => ({ ...f, type: v }))} />
            <FacetFilter label={t('dqe.col.severity', 'Severity')} options={facetOptions.severity}  selected={facets.severity}  onChange={v => setFacets(f => ({ ...f, severity: v }))} />
            <FacetFilter label={t('dqe.col.on', 'On')}             options={facetOptions.appliesOn} selected={facets.appliesOn} onChange={v => setFacets(f => ({ ...f, appliesOn: v }))} />
            <FacetFilter label={t('dqe.col.active', 'Active')}     options={facetOptions.active}    selected={facets.active}    onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('dqe.working', 'Working…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={[...RULE_GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1" title={t('dqe.col.related', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('dqe.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.data_quality_rule_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(r.data_quality_rule_id)}
                        hasChildren={hasRelated(r.data_quality_rule_id)}
                        onToggle={() => detail.toggle(r.data_quality_rule_id)}
                        expandLabel={t('dqe.related.expand', 'Show related records')}
                        collapseLabel={t('dqe.related.collapse', 'Hide related')}
                        tooltip={t('dqe.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-2 py-1 text-csm-sm"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono">{r.internal_code}</span>}
                        {col.key === 'entity_name'   && <span className="font-mono">{r.entity_name}</span>}
                        {col.key === 'rule_type'     && <span className="text-csm-xs">{r.rule_type}</span>}
                        {col.key === 'severity'      && (
                          <StatusBadge
                            label={SEVERITY_META[r.severity] ? t(SEVERITY_META[r.severity].labelKey, SEVERITY_META[r.severity].label) : r.severity}
                            variant={SEVERITY_META[r.severity]?.variant ?? 'neutral'}
                          />
                        )}
                        {col.key === 'applies_on'    && <span className="text-csm-xs">{r.applies_on}</span>}
                        {col.key === 'breach_count'  && (() => {
                          const s = snapshots.get(r.data_quality_rule_id);
                          if (!s) return <span className="text-csm-xs text-csm-watermark">{t('dqe.breach.unscanned', '—')}</span>;
                          if (s.is_evaluable !== 'Y') return <span className="text-csm-xs text-csm-watermark" title={t('dqe.breach.not_evaluable_short', 'Rule cannot be evaluated against the current data.')}>{t('dqe.breach.na', 'n/a')}</span>;
                          return <span className={'text-csm-sm tabular-nums ' + (s.breach_count > 0 ? 'font-semibold text-csm-danger' : 'text-csm-ink-soft')}>{s.breach_count}</span>;
                        })()}
                        {col.key === 'last_scanned_at' && (() => {
                          const s = snapshots.get(r.data_quality_rule_id);
                          return <span className="text-csm-xs text-csm-ink-soft">{s ? fmtDateTime(s.last_scanned_at) : t('dqe.breach.unscanned', '—')}</span>;
                        })()}
                        {col.key === 'is_active'     && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key)   && udcGrid.renderCell(r as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-2 py-1 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(() => {
                          const s = snapshots.get(r.data_quality_rule_id);
                          return s && s.is_evaluable === 'Y' && s.breach_count > 0
                            ? <button onClick={e => { e.stopPropagation(); setReviewTarget(r); }} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:opacity-80">{t('dqe.action.review', 'Review breaches')}</button>
                            : null;
                        })()}
                        {canChange && (<button onClick={e => { e.stopPropagation(); setEditing(r); }} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('dqe.action.edit', 'Edit')}</button>)}
                      </div>
                    </td>
                  </tr>
                  {detail.isOpen(r.data_quality_rule_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r.data_quality_rule_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center">
                    <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('dqe.empty', 'No data quality rules')} action={canAdd ? <button onClick={() => setCreating(true)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('dqe.new_rule_plus', '+ New Rule')}</button> : undefined} />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <RuleModal session={session} row={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }} />
      )}

      {reviewTarget && (
        <DqeBreachReviewModal
          ruleId={reviewTarget.data_quality_rule_id}
          ruleCode={reviewTarget.internal_code}
          ruleType={reviewTarget.rule_type}
          ruleDef={reviewTarget.rule_def}
          onClose={() => setReviewTarget(null)}
        />
      )}
    </div>
  );
}

function RuleModal({ session, row, onClose, onSaved }: {
  session: AuthSession; row: RuleRow | null; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isCreate = row === null;

  const initialDefText = (() => {
    try { return JSON.stringify(row?.rule_def ?? DEFAULT_DEF[row?.rule_type ?? 'REQUIRED'], null, 2); } catch { return '{}'; }
  })();

  const initialValues = {
    code: row?.internal_code ?? '',
    entity: row?.entity_name ?? '',
    ruleType: (row?.rule_type ?? 'REQUIRED') as RuleType,
    severity: (row?.severity ?? 'ERROR') as Severity,
    appliesOn: (row?.applies_on ?? 'BOTH') as AppliesOn,
    errMsg: row?.error_message ?? '',
    active: row ? row.is_active === 'Y' : true,
    description: row?.description ?? '',
    defText: initialDefText,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);
  const [showDiscard, setShowDiscard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Custom fields placed on this entity (host = the list page) become first-class
  // FieldRenderer items: they interleave with native fields and inherit the same
  // per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('dqe_rule', 'DQE.RULES', { pkColumn: 'data_quality_rule_id', pk: row?.data_quality_rule_id });
  const ruleCoreLayout = useMemo(() => [...RULE_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const ruleFieldDefs = useMemo(() => [...RULE_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('DQE.RULES.MODAL', ruleCoreLayout);

  useEffect(() => { reset(initialValues); }, [row?.data_quality_rule_id]); // eslint-disable-line

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  const onTypeChange = (t: RuleType) => {
    setForm({
      ...form,
      ruleType: t,
      // Offer a sensible default skeleton when authoring a new rule.
      ...(isCreate ? { defText: JSON.stringify(DEFAULT_DEF[t], null, 2) } : {}),
    });
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let def: unknown;
      try { def = JSON.parse(form.defText); } catch { throw new Error(t('dqe.err.def_json', 'Rule definition must be valid JSON.')); }
      if (isCreate && !form.code.trim()) throw new Error(t('dqe.err.code_required', 'Internal code is required.'));
      if (!form.entity.trim()) throw new Error(t('dqe.err.entity_required', 'Entity (table) name is required.'));

      const payload = {
        entity_name: form.entity.trim(),
        rule_type: form.ruleType,
        severity: form.severity,
        applies_on: form.appliesOn,
        rule_def: def,
        error_message: form.errMsg || null,
        is_active: form.active ? 'Y' : 'N',
        description: form.description || null,
      };
      if (isCreate) {
        const { error: e } = await routedInsert(session, 'dqe_rule', { internal_code: form.code.trim().toUpperCase(), ...payload });
        if (e) throw e;
      } else {
        const { error: e } = await routedUpdate(session, 'dqe_rule', 'data_quality_rule_id', row?.data_quality_rule_id, payload);
        if (e) throw e;
      }
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <>
    <Modal
      title={isCreate ? t('dqe.modal.create_title', 'Create Data Quality Rule') : t('dqe.modal.edit_title', 'Edit Rule — {{code}}', { code: row?.internal_code ?? '' })}
      onClose={handleClose}
      width="2xl"
      headerAction={session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) ? null : undefined}
      footer={
        <>
          <button onClick={handleClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('dqe.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('dqe.saving', 'Saving…') : t('dqe.save', 'Save')}</button>
        </>
      }
    >
      <div className="mb-3">
      <FieldRenderer
        fields={ruleFieldDefs}
        layout={layout}
        columns={2}
        pageCode="DQE.RULES.MODAL"
        session={session}
        render={(fld) => {
          if (fld.key === 'code') return (
            <PersonalizedField componentKey="modal" fieldKey="code" label={t('dqe.field.code', 'Internal Code')}>
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.field.code', 'Internal Code')}</label>
              <input type="text" value={form.code} disabled={!isCreate}
                     onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                     onBlur={() => {
                       if (isCreate && form.code && !/^[A-Z][A-Z0-9_.]*$/.test(form.code)) {
                         setInternalCodeError(t('dqe.err.code_format', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                       } else { setInternalCodeError(null); }
                     }}
                     className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted" />
              {internalCodeError
                ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('dqe.hint.code', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
              }
            </div>
            </PersonalizedField>
          );
          if (fld.key === 'entity') return (
            <PersonalizedField componentKey="modal" fieldKey="entity" label={t('dqe.field.entity', 'Entity (table)')}>
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.field.entity', 'Entity (table)')}</label>
              <input type="text" value={form.entity} onChange={e => setForm({ ...form, entity: e.target.value })} placeholder="csm_user_master"
                     className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none" />
            </div>
            </PersonalizedField>
          );
          if (fld.key === 'ruleType') return (
            <PersonalizedField componentKey="modal" fieldKey="ruleType" label={t('dqe.field.rule_type', 'Rule Type')}>
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.field.rule_type', 'Rule Type')}</label>
              <select value={form.ruleType} onChange={e => onTypeChange(e.target.value as RuleType)}
                      className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none">
                <option value="REQUIRED">REQUIRED</option>
                <option value="FORMAT">FORMAT</option>
                <option value="CROSS_FIELD">CROSS_FIELD</option>
              </select>
            </div>
            </PersonalizedField>
          );
          if (fld.key === 'severity') return (
            <PersonalizedField componentKey="modal" fieldKey="severity" label={t('dqe.field.severity', 'Severity')}>
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.field.severity', 'Severity')}</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value as Severity })}
                      className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none">
                <option value="ERROR">{t('dqe.severity.error', 'ERROR (blocks)')}</option>
                <option value="WARNING">{t('dqe.severity.warning', 'WARNING (passes)')}</option>
              </select>
            </div>
            </PersonalizedField>
          );
          if (fld.key === 'appliesOn') return (
            <PersonalizedField componentKey="modal" fieldKey="appliesOn" label={t('dqe.field.applies_on', 'Applies On')}>
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.field.applies_on', 'Applies On')}</label>
              <select value={form.appliesOn} onChange={e => setForm({ ...form, appliesOn: e.target.value as AppliesOn })}
                      className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none">
                <option value="BOTH">BOTH</option>
                <option value="ADD">ADD</option>
                <option value="CHANGE">CHANGE</option>
              </select>
            </div>
            </PersonalizedField>
          );
          if (udcForm.isUdcKey(fld.key)) return (
            <PersonalizedField componentKey="modal" fieldKey={fld.key} label={fld.label}
              readonlyChildren={udcForm.renderControl(fld.key, { disabled: true })}>
              {udcForm.renderControl(fld.key)}
            </PersonalizedField>
          );
          return null;
        }}
      />
      </div>

      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.label.rule_definition', 'Rule Definition (JSON)')}</label>
      <textarea rows={8} value={form.defText} onChange={e => setForm({ ...form, defText: e.target.value })}
                className="mb-1 w-full rounded border border-csm-border px-2 py-1 font-mono text-csm-xs focus:border-csm-watermark focus:outline-none" />
      <div className="mb-3 text-csm-2xs text-csm-ink-soft">
        REQUIRED: <span className="font-mono">{'{"column":"x"}'}</span>.
        FORMAT: <span className="font-mono">{'{"column":"x","format":"email|phone|url|date|numeric|..."}'}</span> or <span className="font-mono">"regex"</span>.
        CROSS_FIELD: <span className="font-mono">compare</span> / <span className="font-mono">require_if</span> predicate.
      </div>

      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.label.error_message', 'Custom Error Message (optional)')}</label>
      <input type="text" value={form.errMsg} onChange={e => setForm({ ...form, errMsg: e.target.value })}
             className="mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none" />

      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dqe.label.description', 'Description')}</label>
      <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="mb-3 w-full rounded border border-csm-border px-2 py-1 text-csm-sm focus:border-csm-watermark focus:outline-none" />

      <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
        <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-gray-700" /> {t('dqe.active', 'Active')}
      </label>
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>

    {showDiscard && (
      <ConfirmModal
        title={t('dqe.discard.title', 'Discard changes?')}
        message={t('dqe.discard.message', 'You have unsaved changes that will be lost.')}
        confirmLabel={t('dqe.discard.confirm', 'Discard')}
        cancelLabel={t('dqe.discard.cancel', 'Keep editing')}
        danger={false}
        onConfirm={() => { reset(); onClose(); }}
        onCancel={() => setShowDiscard(false)}
      />
    )}
    </>
  );
}
